import React from 'react';
import { GameData } from '../share/game-data.js'
import BuildingCostFormulas from '../share/formulas/building-cost-formulas.js';
import * as ICONS from "../other/icons.js";
import * as util from "../other/util.js";
import ResourceVector from "../share/resource-vector.js";
import ResourceProductionFormulas from "../share/formulas/resource-production-formulas.js";
import CompactResourcesView from "./compact-resources-view.js";

// Shows information (cost, build time) about a given building
class BuildingInfoView extends React.Component {
    constructor(props) {
        super(props);
        this.state = { central_level: 1, same_level_as_central: true };
    }

    render() {
        const building_id = this.props.params.building_id;
        const building = GameData.BUILDING_BY_ID[building_id];

        if (!building) {
            return (<util.LoadingBar thing={building} thingName="building" />);
        }

        return (
            <div>
                <div style={{ display: "flex", gap: "1em" }}>
                    {/* left column: image */}
                    <div style={{ width: "250px", flexShrink: 0 }}>
                        <img
                            src={ICONS.BUILDINGS[building.id]}
                            alt={building.ui_name}
                            style={{ width: "100%", marginBottom: "-4px", marginTop: "20px" }}
                        />
                    </div>

                    {/* right column: name, description */}
                    <div style={{ flex: 1 }}>
                        <h2>{building.ui_name}</h2>
                        <p>{building.description}</p>
                        {building.id === "central" ? <CentralBuildingInfoView building={building} /> : null}
                    </div>
                </div>

                <h4>Building Cost</h4>
                <p className="boring-info-text">Shows the cost/time to build each level of this building. If the checkbox is set, it assumes that the HQ is on the same level.</p>
                <util.KeyCheckboxInput label="Use same HQ level as building" value={this.state.same_level_as_central} onChange={((val) => {
                    this.onChangeSame(val)
                })} />
                {
                    this.state.same_level_as_central ? undefined : (<util.KeyValueInput width="50px" checks={["NUMBER"]} value={this.state.central_level} label={GameData.buildingName("central") + " level"} onChange={(val) => {
                        this.onChangeCentral(val)
                    }} />)
                }
                <br />
                <div>
                    <BuildingInfoTable building={building}
                        central_level={this.state.central_level}
                        same_level_as_central={this.state.same_level_as_central} />
                </div>
            </div >
        );
    }

    onChangeCentral(val) {
        this.setState({ central_level: val });
    }

    onChangeSame(val) {
        this.setState({ same_level_as_central: val });
    }
}

class BuildingInfoTable extends React.Component {
    render() {
        const building = this.props.building;
        const production = new ResourceVector().init().initSparse(building.production ? building.production(1) : new ResourceVector());
        const is_production_building = production.sumUpVector() > 0;
        const is_housing_building = building.id === "housing";
        const has_energy_production = building.energy_production_factor;
        const is_storage_building = building.id === "storage";
        const has_worker_production = building.worker_production_const || building.worker_replication_factor;
        const is_central_building = building.id === "central";
        const show_production = !is_central_building && (is_production_building || has_energy_production || has_worker_production);

        let levels = Array.from(Array(101).keys());
        levels.shift(); // You can not build level 0

        return (
            <div>
                <table className="resources" style={{ width: "95%" }}>
                    <thead>
                        <tr>
                            <td><div>Level</div></td>
                            <td><div>Construction Cost</div></td>
                            <td><div>Construction Time</div></td>
                            {show_production ? <td><div>Production</div></td> : null}
                            {is_housing_building ? <td><div>Housing Capacity</div></td> : null}
                            {is_storage_building ? <td><div>Storage Capacity</div></td> : null}
                        </tr>
                    </thead>
                    <tbody>
                        {levels.map((level) => {
                            return (<BuildingInfoRow key={level}
                                level={level}
                                building={building}
                                central_level={this.props.central_level}
                                same_level_as_central={this.props.same_level_as_central}
                                is_production_building={is_production_building}
                                is_housing_building={is_housing_building}
                                is_storage_building={is_storage_building} />);
                        })}
                    </tbody>
                </table>
            </div>);
    }
}

class BuildingInfoRow extends React.Component {
    render() {
        const level = this.props.level;
        const building = this.props.building;
        const central_level = this.props.same_level_as_central ? level : this.props.central_level;

        // Normal columns: cost, time
        const cost = BuildingCostFormulas.getCostTotal(building, level);
        const time = BuildingCostFormulas.getTime(building, level, central_level);
        const energy_consumption_stats = { "amount": BuildingCostFormulas.getEnergyCost(building, level) };

        // Special column: regular production buildings
        const is_production_building = this.props.is_production_building;
        const production = ResourceProductionFormulas.getRawBuildingProduction(building, level);

        // Special column: worker
        const is_housing_building = this.props.is_housing_building;
        const housing_capacity = ResourceProductionFormulas.getRawHousingCapacity(level);

        // Add energy production if available
        const energy_production = ResourceProductionFormulas.getRawBuildingEnergyProduction(building, level);
        const energy_production_stats = { "amount": energy_production };
        const has_energy_production = energy_production > 0;

        // Add worker production if available
        const has_worker_production = building.worker_production_const || building.worker_replication_factor;
        production.worker = ResourceProductionFormulas.getRawWorkerProduction(building, level, housing_capacity, 50);

        // Special column: storage
        const is_storage_building = this.props.is_storage_building;
        const storage_capacity = is_storage_building ? ResourceProductionFormulas.getRawStorageCapacity(level) : 0;

        // Production column visibility
        const is_central_building = building.id === "central";
        const show_production = !is_central_building && (is_production_building || has_energy_production || has_worker_production);

        return (
            <tr>
                <td style={{ textAlign: "right" }}>{level}</td>
                <td style={{ textAlign: "right" }}><CompactResourcesView resources={cost} energy_stats={energy_consumption_stats} /></td>
                <td style={{ textAlign: "right" }}>
                    <util.TimeSpan timeSpan={time} />
                </td>
                {show_production ? <td style={{ textAlign: "right" }}><CompactResourcesView resources={production} energy_stats={energy_production_stats} /></td> : null}
                {is_housing_building ? <td style={{ textAlign: "right" }}><util.ShortNumber number={housing_capacity} /></td> : null}
                {is_storage_building ? <td style={{ textAlign: "right" }}><util.ShortNumber number={storage_capacity} /></td> : null}
            </tr>)
    }
}

class CentralBuildingInfoView extends React.Component {
    render() {
        const building = this.props.building;

        const production = ResourceProductionFormulas.getRawBuildingProduction(building, 1);
        production.worker = ResourceProductionFormulas.getRawWorkerProduction(building, 1, 0, 50);
        const energy_production = ResourceProductionFormulas.getRawBuildingEnergyProduction(building, 1);
        const energy_production_stats = { "amount": energy_production };

        return (
            <div>
                <p>Once build, the {building.ui_name} always produces the following resources, independent of it's level:</p>
                <CompactResourcesView resources={production} energy_stats={energy_production_stats} />
            </div>
        );
    }
}

export default util.withParams(BuildingInfoView);
